import type { MediaImage as GeneratedMediaImageProps } from "@homeinstead/schemas";

import { cn } from "@homeinstead/ui";
import {
  getMediaAspectClass,
  MediaFigure,
} from "@homeinstead/ui/components/Media";
import { SanityImage } from "~www/components/SanityImage";

type MediaImageProps = Omit<GeneratedMediaImageProps, "_type"> & {
  className?: string;
};

/**
 * Editorial image. Lives in the app rather than `packages/ui` because building
 * a Sanity CDN URL goes through the project-configured `urlFor` builder.
 *
 * A mobile crop is rendered as a second image toggled by breakpoint rather
 * than a `<picture>` element, so both go through `next/image` optimization.
 */
function MediaImage({
  aspectRatio,
  className,
  image,
  loading = "lazy",
  mobileImage,
  priority,
}: MediaImageProps) {
  if (!image?.asset?._ref) {
    return null;
  }

  // Always pass a string so the <img> keeps an alt attribute. An empty value
  // is decorative (`alt=""`), not a missing attribute.
  const alt = image.alt ?? "";
  const aspectClass = getMediaAspectClass(aspectRatio);
  const imageClassName = cn(
    "rounded-lg",
    aspectClass ? cn(aspectClass, "object-cover") : undefined,
  );
  const hasMobileVariant = Boolean(mobileImage?.asset?._ref);

  return (
    <MediaFigure className={className}>
      <SanityImage
        alt={alt}
        asset={image}
        className={cn(imageClassName, hasMobileVariant && "hidden md:block")}
        loading={priority ? undefined : loading}
        priority={priority}
      />

      {hasMobileVariant ? (
        <SanityImage
          alt={alt}
          asset={mobileImage}
          className={cn(imageClassName, "md:hidden")}
          loading={priority ? undefined : loading}
          priority={priority}
        />
      ) : null}
    </MediaFigure>
  );
}

export { MediaImage };
